import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import isEmpty from "lodash/isEmpty";
import { v4 as uuidv4 } from "uuid";
import Table from "../Table";
import ToggleButton from "../ToggleButton/ToggleButton";
import Pagination from "../Pagination/Pagination";
import withErrorGuard from "../../../HOC/withErrorGuard";
import ls from "../../../common/helpers/localStorageActions";
import LinkifyBase from "../../social/LinkifyBase";

const ApprovedTherapiesTable = ({ approvedTherapies }) => {
  const [showApprovedTherapiesFullDetail, setApprovedTherapiesFullDetail] =
    useState(ls.getAmpApprovedTherapiesFullDetail() || false);
  const [pageSize, setPageSize] = useState(5);
  const pageSizeOptions = [5, 10, 20, 30, 50];
  const [page, setPage] = useState(1);
  const [visibleApprovedTherapies, setVisibleApprovedTherapies] = useState([]);
  const hasTherapies = !isEmpty(approvedTherapies);

  const updatePage = useCallback((pageNum) => {
    setPage(pageNum);
  }, []);

  const onPageSizeUpdate = useCallback((pSize) => {
    setPage(1);
    setPageSize(pSize);
  }, []);

  useEffect(() => {
    const items = approvedTherapies?.slice(
      pageSize * (page - 1),
      pageSize * page
    );
    setVisibleApprovedTherapies([...items]);
  }, [approvedTherapies, page, pageSize]);

  if (!hasTherapies) return null;

  const fullDetailHeaders = [
    {
      id: "therapy-column",
      title: "Approved Therapies",
      additionalStyles: { width: "12rem" },
    },
    {
      id: "therapy-info-column",
      title: "",
      additionalStyles: { width: "20rem" },
    },
    { id: "drugs-column", title: "Drugs" },
    { id: "efficacy-column", title: "Efficacy Evidence" },
    { id: "therapy-descriptions-column", title: "Therapy Descriptions" },
  ];

  const reducedDetailHeaders = [
    {
      id: "therapy-column",
      title: "Approved Therapies",
      additionalStyles: { width: "22rem" },
    },
    { id: "indication", title: "Indication" },
    { id: "molecular-profile", title: "Molecular Profile" },
    { id: "response-type", title: "Response Type" },
    { id: "drugs-column", title: "Drugs" },
  ];

  const getTherapyCell = (t) => {
    return {
      id: `${t.therapy_id}-therapy`,
      content: (
        <div className="tw-flex tw-flex-col">
          <div className="tw-flex tw-text-base tw-font-bold tw-text-gray-600 tw-flex-wrap">
            {t.therapy}
          </div>
          <div className="tw-italic tw-text-gray-600">{t.approval_status}</div>
        </div>
      ),
    };
  };

  const getDrugCell = (t) => {
    return {
      id: `${t.therapy_id}-drugs`,
      content: (
        <div className="tw-flex tw-flex-row tw-flex-wrap">
          {t.drugs
            ? t.drugs.map((d, index) => (
                <div
                  id={`${index}${d}`}
                  className="tw-py-0.5 tw-px-1.5 tw-mr-2 tw-mb-2 tw-font-semibold tw-text-xs tw-rounded-sm tw-text-white tw-bg-blue-400"
                >
                  {d.drug_name}
                </div>
              ))
            : "-"}
        </div>
      ),
    };
  };

  const fullDetailRows = visibleApprovedTherapies.map((t) => ({
    id: uuidv4(),
    columns: [
      {
        ...getTherapyCell(t),
      },
      {
        id: `${t.therapy_id}-therapy-info`,
        content: (
          <div className="tw-flex tw-flex-col">
            {[
              { title: "Indication", data: t.indication },
              { title: "Molecular Profile", data: t.molecular_profile },
              { title: "Response Type", data: t.response_type },
            ].map((i, index) => (
              <div id={`${i.title}${index}`} className="tw-py-1">
                <div className="tw-text-xs tw-font-semibold tw-text-gray-600">
                  <span>{i.title}</span>
                </div>
                <div className="tw-text-sm">{i.data}</div>
              </div>
            ))}
          </div>
        ),
      },
      {
        ...getDrugCell(t),
      },
      {
        id: `${t.therapy_id}-efficacy-evidence`,
        content: (
          <span>
            <LinkifyBase txt={t.efficacy_evidence} />
          </span>
        ),
        className: "tw-align-top",
      },
      {
        id: `${t.therapy_id}-therapy-descriptions`,
        content: (
          <div>
            {t.therapy_descriptions
              ? t.therapy_descriptions.map((d, index) => (
                  <span id={`${index}${t.therapy_id}`}>
                    <LinkifyBase txt={d.description} />
                  </span>
                ))
              : ""}
          </div>
        ),
        className: "tw-align-top",
      },
    ],
  }));

  const reducedDetailRows = visibleApprovedTherapies.map((t) => ({
    id: uuidv4(),
    columns: [
      {
        ...getTherapyCell(t),
      },
      {
        id: `${t.therapy_id}-indication`,
        content: <span>{t.indication}</span>,
      },
      {
        id: `${t.therapy_id}-molecular-profile`,
        content: <span>{t.molecular_profile}</span>,
      },
      {
        id: `${t.therapy_id}-response-type`,
        content: <span>{t.response_type}</span>,
      },
      {
        ...getDrugCell(t),
      },
    ],
  }));

  const headers = showApprovedTherapiesFullDetail
    ? fullDetailHeaders
    : reducedDetailHeaders;

  const rows = showApprovedTherapiesFullDetail
    ? fullDetailRows
    : reducedDetailRows;

  return (
    <div className="tw-my-8">
      <div className="tw-flex tw-my-3 tw-items-center tw-justify-end">
        <ToggleButton
          onToggleClick={(e) => {
            setApprovedTherapiesFullDetail(e.target.checked);
            ls.setAmpApprovedTherapiesFullDetail(e.target.checked);
          }}
          label={`${
            showApprovedTherapiesFullDetail ? "Hide" : "Show"
          } full detail`}
          isChecked={showApprovedTherapiesFullDetail}
          noPaddingAndMargin={true}
        />
      </div>
      <Table headers={headers} rows={rows} />
      <div className="tw-pt-4">
        <Pagination
          currentPage={page}
          totalResults={approvedTherapies.length}
          pageSize={pageSize}
          onPageChange={updatePage}
          pageSizeOptions={pageSizeOptions}
          onPageSizeChange={onPageSizeUpdate}
        />
      </div>
    </div>
  );
};

ApprovedTherapiesTable.propTypes = {
  approvedTherapies: PropTypes.arrayOf(PropTypes.shape({})),
};

ApprovedTherapiesTable.defaultProps = {
  approvedTherapies: [],
};

export default withErrorGuard(
  ApprovedTherapiesTable,
  "ApprovedTherapiesTableComponent",
  "Approved Therapies"
);
